import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const NAV = [
  { label: "Work", id: "work" },
  { label: "About", id: "about" },
  { label: "Founder", id: "founder" },
  { label: "Services", id: "services" },
  { label: "Contact", id: "contact" },
];

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ#%&/";

function useScramble(base, resolved) {
  const [text, setText] = useState(base);
  const raf = useRef(null);
  const run = (target) => {
    cancelAnimationFrame(raf.current);
    const start = performance.now();
    const dur = 400;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const reveal = Math.floor(p * target.length);
      let out = "";
      for (let i = 0; i < target.length; i++) {
        out += i < reveal ? target[i] : (target[i] === " " ? " " : CHARS[Math.floor(Math.random() * CHARS.length)]);
      }
      setText(out);
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else setText(target);
    };
    raf.current = requestAnimationFrame(tick);
  };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  return { text, toResolved: () => run(resolved), toBase: () => run(base) };
}

function Logo({ onClick }) {
  return (
    <button onClick={onClick} data-testid="header-logo" aria-label="Qissa home" className="pointer-events-auto block">
      <img src="/qissa-logo.png" alt="Qissa" className="h-10 md:h-11 w-auto" style={{ filter: "invert(1)" }} />
    </button>
  );
}

export default function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const cta = useScramble("WRITE TO US", "LET'S TALK");

  const goHome = () => {
    setMenuOpen(false);
    if (pathname === "/") {
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1 });
      else window.scrollTo(0, 0);
    } else {
      navigate("/");
    }
  };

  const goTo = (id) => {
    setMenuOpen(false);
    if (id === "work") { navigate("/work"); return; }
    if (id === "about") { navigate("/about"); return; }
    if (pathname === "/") {
      const el = document.getElementById(id);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.2 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <>
      <header data-testid="site-header"
        className="fixed top-0 left-0 w-full z-50 mix-blend-difference text-white pointer-events-none">
        <div className="px-4 md:px-6 py-4 md:py-5 flex items-start justify-between gap-6">
          {/* left: logo */}
          <div className="flex-shrink-0">
            <Logo onClick={goHome} />
          </div>

          {/* left-centre: studio descriptor (desktop) -> home */}
          <button onClick={goHome} data-testid="header-studio-block"
            className="hidden lg:block text-left pointer-events-auto font-mono uppercase text-[11px] leading-[1.4] tracking-[0.05em] pt-1 mr-auto ml-[6vw]">
            Qissa Studio<br />Branding, social and web<br />studio based in New Delhi
          </button>

          {/* right: nav + cta (desktop) */}
          <nav className="hidden md:flex items-center gap-7 pointer-events-auto pt-1">
            <div className="font-serif text-[15px] flex items-center gap-6">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => goTo(n.id)}
                  className="hover:underline underline-offset-4 decoration-1 transition-opacity hover:opacity-100 opacity-90">
                  {n.label}
                </button>
              ))}
            </div>
            <button onClick={() => { setMenuOpen(false); navigate("/connect"); }} data-testid="header-cta"
              onMouseEnter={cta.toResolved} onMouseLeave={cta.toBase}
              className="pointer-events-auto font-serif font-semibold text-[15px] tracking-tight whitespace-nowrap min-w-[108px] text-right">
              {cta.text}
            </button>
          </nav>

          {/* mobile hamburger */}
          <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen(true)}
            className="md:hidden pointer-events-auto flex flex-col gap-[5px] pt-1.5" aria-label="Open menu">
            <span className="block w-6 h-px bg-current" />
            <span className="block w-6 h-px bg-current" />
          </button>
        </div>
      </header>

      {/* mobile full-screen overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-white text-black flex flex-col">
            <div className="px-4 py-4 flex items-center justify-between">
              <button onClick={goHome} data-testid="mobile-logo-home" aria-label="Qissa home">
                <img src="/qissa-logo.png" alt="Qissa" className="h-10 w-auto" />
              </button>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu"
                className="font-mono uppercase text-xs tracking-[0.12em]">Close</button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2 px-6">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`mobile-nav-${n.id}`} onClick={() => goTo(n.id)}
                  className="font-display uppercase text-5xl tracking-[-0.02em] leading-none text-left py-1">
                  {n.label}
                </button>
              ))}
            </div>
            <div className="px-6 pb-10">
              <button onClick={() => { setMenuOpen(false); navigate("/connect"); }} className="font-serif text-xl underline underline-offset-4">Let's talk</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
